import { User } from '../../../../types/user';

// Status types
export type ReviewRecordStatus = 'pending' | 'completed' | 'failed' | 'insufficient_data';

// Visibility types
export type ReviewRecordVisibility = 'public' | 'private';

// Structured Content Types (for openai_beta agent)
/**
 * Status values that affect UI behavior (keep strict for conditional logic)
 */
export type EntryStatus = 'complete' | 'needs_context' | 'insufficient_data';
export type DataCoverage = 'complete' | 'partial' | 'insufficient';

/**
 * Data reference types (keep strict - used for linking to actual records)
 */
export type DataReferenceType = 'recognition' | 'goal' | 'meeting' | 'feedback' | 'agenda_item';

/**
 * Person involved in an entry (who worked with)
 */
export interface PersonReference {
	id: string;
	name: string | null;
	email: string | null;
	relationship: string | null; // Generic string - backend determines values
}

/**
 * Person to follow up with (aggregated from all entries)
 */
export interface PersonToFollowUp {
	id: string;
	name: string | null;
	email: string | null;
	mentions: number;
	roles: (string | null)[];
}

/**
 * Reference to source data (recognition, meeting, etc.)
 */
export interface DataReference {
	type: DataReferenceType;
	id: string;
}

/**
 * Example with quote and context from source data
 */
export interface Example {
	quote: string;
	context: string;
	data_reference: DataReference;
}

/**
 * Individual entry within a section (e.g., achievement, behavior, outcome)
 */
export interface Entry {
	id: string;
	item_kind: string; // Generic string - mostly for display/categorization
	how: string; // How they worked (style, behaviors, collaboration)
	what: string; // What they worked on (project, initiative, outcome)
	who: PersonReference[]; // Who they worked with
	title: string;
	summary: string;
	examples: Example[];
	status: EntryStatus;
	status_message: string | null;
}

/**
 * Structured content for a section (stored in ReviewRecord.section_responses[].structured_content)
 * Note: section_id and answer_summary are stored at the SectionResponse level, not here
 * This matches line 414: structured_content = section_data.except("section_id", "answer_summary")
 */
export interface StructuredContent {
	inferred_section_kind: string | null; // Generic string - mostly informational
	inferred_section_kind_confidence: number | null; // 0.0-1.0
	entries: Entry[];
	people_to_follow_up: PersonToFollowUp[];
	data_coverage: DataCoverage;
	data_coverage_note: string | null;
}

// Core interfaces
export interface ToolCall {
	tool: string;
	status: 'success' | 'error';
	arguments?: Record<string, any>;
	timestamp: string; // ISO 8601
	error?: string;
}

export interface AssociatedData {
	type: string;
	id: string;
}

// Raw API response for section (snake_case)
export interface SectionResponseApiData {
	id: string;
	question: string;
	section_title: string;
	section_guidance: string;
	answer_format: string;
	answer?: string;

	// New fields for openai_beta agent
	answer_summary?: string; // OPTIONAL: concise 1-2 paragraph summary
	structured_content?: StructuredContent; // OPTIONAL: structured data for beta agent

	// Tool usage tracking
	tool_calls: ToolCall[];
	tools_used: string[];
	associated_data: AssociatedData[];

	// Processing info
	processing_time_seconds: number;
	failed: boolean;
	error?: string;
}

// Transformed interface (camelCase)
export interface SectionResponse {
	id: string;
	question: string;
	sectionTitle: string;
	sectionGuidance: string;
	answerFormat: string;
	answer?: string;

	// New fields for openai_beta agent
	answerSummary?: string; // OPTIONAL: concise 1-2 paragraph summary
	structuredContent?: StructuredContent; // OPTIONAL: structured data for beta agent

	// Tool usage tracking
	toolCalls: ToolCall[];
	toolsUsed: string[];
	associatedData: AssociatedData[];

	// Processing info
	processingTimeSeconds: number;
	failed: boolean;
	error?: string;
}

export interface ReviewTemplate {
	id: string;
	name: string;
	status: string;
	schema: Record<string, any>;
}

// Raw API response interface (snake_case)
export interface ReviewRecordApiData {
	// Identifiers
	id: string;
	company_id: string;
	user_id: string;
	generated_by_user_id: string;
	title?: string;

	// Template relationship
	template_id: string;
	template_version?: number;

	// Time period
	time_period_start: string; // ISO 8601 date
	time_period_end: string; // ISO 8601 date

	// Status and timing
	status: ReviewRecordStatus;
	generation_started_at?: string; // ISO 8601 timestamp
	generation_completed_at?: string; // ISO 8601 timestamp
	generation_error?: string;

	// Timestamps
	created_at: string; // ISO 8601 timestamp
	updated_at: string; // ISO 8601 timestamp

	// Computed template info
	template_name: string;
	template_status: string;

	// Section metrics
	total_sections: number;
	total_successful_sections: number;
	total_failed_sections: number;

	// Status helpers
	pending: boolean;
	completed: boolean;
	failed: boolean;
	generation_in_progress: boolean;

	// Visibility
	visibility: ReviewRecordVisibility;

	// Review Agent
	review_agent?: string;

	// Preview flag
	is_preview?: boolean;

	// Test flag
	is_test?: boolean;

	// User associations
	user: User;
	generated_by_user: User;

	// Abilities added by backend under `abilities`
	abilities?: {
		canView?: boolean;
		canUpdate?: boolean;
		canUpdateVisibility?: boolean;
		canDelete?: boolean;
	};
}

// Full API response interface (includes additional fields)
export interface FullReviewRecordApiData extends ReviewRecordApiData {
	// Additional content fields
	section_responses: SectionResponseApiData[];
	available_tools: string[];

	// Detailed metrics
	total_tool_calls_made: number;
	successful_tool_calls_made: number;
	failed_tool_calls_made: number;
	total_processing_time: number;
	total_associated_data_count: number;
	unique_tools_used: string[];

	// Template association
	review_template: ReviewTemplate;
}

// Transformed interfaces (camelCase)
export interface ReviewRecord {
	// Identifiers
	id: string;
	companyId: string;
	userId: string;
	generatedByUserId: string;
	title?: string;

	// Template relationship
	templateId: string;
	templateVersion?: number;

	// Time period
	timePeriodStart: string; // ISO 8601 date
	timePeriodEnd: string; // ISO 8601 date

	// Status and timing
	status: ReviewRecordStatus;
	generationStartedAt?: string; // ISO 8601 timestamp
	generationCompletedAt?: string; // ISO 8601 timestamp
	generationError?: string;

	// Timestamps
	createdAt: string; // ISO 8601 timestamp
	updatedAt: string; // ISO 8601 timestamp

	// Computed template info
	templateName: string;
	templateStatus: string;

	// Section metrics
	totalSections: number;
	totalSuccessfulSections: number;
	totalFailedSections: number;

	// Status helpers
	pending: boolean;
	completed: boolean;
	failed: boolean;
	generationInProgress: boolean;

	// Visibility
	visibility: ReviewRecordVisibility;

	// Review Agent
	reviewAgent?: string;

	// Preview flag
	isPreview?: boolean;

	// Test flag
	isTest?: boolean;

	// User associations
	user: User;
	generatedByUser: User;

	// Abilities
	abilities: {
		canView: boolean;
		canUpdate: boolean;
		canUpdateVisibility: boolean;
		canDelete: boolean;
	};
}

export interface FullReviewRecord extends ReviewRecord {
	// Additional content fields
	sectionResponses: SectionResponse[];
	availableTools: string[];

	// Detailed metrics
	totalToolCallsMade: number;
	successfulToolCallsMade: number;
	failedToolCallsMade: number;
	totalProcessingTime: number;
	totalAssociatedDataCount: number;
	uniqueToolsUsed: string[];

	// Template association
	reviewTemplate: ReviewTemplate;
}

// Request types
export interface CreateReviewRecordRequest {
	timePeriodStart: string; // ISO 8601 date
	timePeriodEnd: string; // ISO 8601 date
	userId?: string; // optional, defaults to authenticated user
	title?: string;
	visibility?: ReviewRecordVisibility; // optional, defaults to "public"
	review_agent?: string;
}

export interface UpdateReviewRecordRequest {
	title?: string;
	sectionResponses?: SectionResponse[];
	visibility?: ReviewRecordVisibility;
	isTest?: boolean;
}

export interface ReviewRecordsQueryParams {
	limit?: number; // default: 10, max: 50
	userId?: string;
	status?: ReviewRecordStatus;
	timePeriodStart?: string; // ISO 8601 date
	timePeriodEnd?: string; // ISO 8601 date
}

// API Response types
export type ReviewRecordsIndexResponse = ReviewRecord[];
export type CreateReviewRecordResponse = FullReviewRecord;
export type ShowReviewRecordResponse = FullReviewRecord;
export type UpdateReviewRecordResponse = FullReviewRecord;
export type DeleteReviewRecordResponse = { message: string };
export type RetryReviewRecordResponse = FullReviewRecord;

// New endpoint response types
export type RecordsEnabledResponse = {
	enabled: boolean;
};

export type AvailableTemplate = {
	id: string;
	name: string;
	version: number;
	status: 'active' | 'published';
};

export type AvailableTemplatesResponse = {
	templates: AvailableTemplate[];
};

// Utility class for transforming API data
export class ReviewRecordTransformer {
	static transformSectionResponse(apiData: SectionResponseApiData): SectionResponse {
		return {
			id: apiData.id,
			question: apiData.question,
			sectionTitle: apiData.section_title,
			sectionGuidance: apiData.section_guidance,
			answerFormat: apiData.answer_format,
			answer: apiData.answer,
			answerSummary: apiData.answer_summary,
			structuredContent: apiData.structured_content,
			toolCalls: apiData.tool_calls,
			toolsUsed: apiData.tools_used,
			associatedData: apiData.associated_data,
			processingTimeSeconds: apiData.processing_time_seconds,
			failed: apiData.failed,
			error: apiData.error,
		};
	}

	static fromApiData(apiData: ReviewRecordApiData): ReviewRecord {
		return {
			id: apiData.id,
			companyId: apiData.company_id,
			userId: apiData.user_id,
			generatedByUserId: apiData.generated_by_user_id,
			templateId: apiData.template_id,
			templateVersion: apiData.template_version,
			title: apiData.title,
			timePeriodStart: apiData.time_period_start,
			timePeriodEnd: apiData.time_period_end,
			status: apiData.status,
			generationStartedAt: apiData.generation_started_at,
			generationCompletedAt: apiData.generation_completed_at,
			generationError: apiData.generation_error,
			createdAt: apiData.created_at,
			updatedAt: apiData.updated_at,
			templateName: apiData.template_name,
			templateStatus: apiData.template_status,
			totalSections: apiData.total_sections,
			totalSuccessfulSections: apiData.total_successful_sections,
			totalFailedSections: apiData.total_failed_sections,
			pending: apiData.pending,
			completed: apiData.completed,
			failed: apiData.failed,
			generationInProgress: apiData.generation_in_progress,
			visibility: apiData.visibility,
			reviewAgent: apiData.review_agent,
			isPreview: apiData.is_preview,
			isTest: apiData.is_test,
			user: apiData.user,
			generatedByUser: apiData.generated_by_user,
			// Abilities default to false if not provided
			abilities: {
				canView: !!apiData.abilities?.canView,
				canUpdate: !!apiData.abilities?.canUpdate,
				canUpdateVisibility: !!apiData.abilities?.canUpdateVisibility,
				canDelete: !!apiData.abilities?.canDelete,
			},
		};
	}

	static fromFullApiData(apiData: FullReviewRecordApiData): FullReviewRecord {
		const baseRecord = this.fromApiData(apiData);
		return {
			...baseRecord,
			sectionResponses: apiData.section_responses.map(this.transformSectionResponse),
			availableTools: apiData.available_tools,
			totalToolCallsMade: apiData.total_tool_calls_made,
			successfulToolCallsMade: apiData.successful_tool_calls_made,
			failedToolCallsMade: apiData.failed_tool_calls_made,
			totalProcessingTime: apiData.total_processing_time,
			totalAssociatedDataCount: apiData.total_associated_data_count,
			uniqueToolsUsed: apiData.unique_tools_used,
			reviewTemplate: apiData.review_template,
		};
	}

	static toCreateApiData(request: CreateReviewRecordRequest): Record<string, any> {
		const payload: Record<string, any> = {
			time_period_start: request.timePeriodStart,
			time_period_end: request.timePeriodEnd,
			user_id: request.userId,
			title: request.title,
			visibility: request.visibility || 'public',
		};

		// Only include review_agent if it's explicitly provided
		if (request.review_agent !== undefined) {
			payload.review_agent = request.review_agent;
		}

		return payload;
	}

	static toUpdateApiData(request: UpdateReviewRecordRequest): Record<string, any> {
		const payload: Record<string, any> = {};

		if (request.sectionResponses !== undefined) payload.section_responses = request.sectionResponses;
		if (request.title !== undefined) payload.title = request.title;
		if (request.visibility !== undefined) payload.visibility = request.visibility;
		if (request.isTest !== undefined) payload.is_test = request.isTest;

		return payload;
	}
}
